import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, Collapse, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendIcon from '@mui/icons-material/Send';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useOnboarding } from '../../../contexts/onboarding.context';
import OnbFrame from '../_shared/OnbFrame';
import { MbTextField } from '../../../components/MbTextFields';
import PhoneWithPush from '../_shared/PhoneWithPush';
import { COLORS } from '../_shared/tokens';
import { mobilizeiDevicesService } from '../../../services/mobilizei/devices.service';
import { showApiError } from '../../../utils/errors';
import useAppInfo from '../../../hooks/appInfo.hook';
import mixpanel from 'mixpanel-browser';
import { mixpanelEvents } from '../../../utils/constants';

const TITLE_MAX = 40;
const TEXT_MAX = 120;

const pushTitle = 'Novidades no nosso catálogo';
const pushText = 'Toque para conferir tudo o que temos de novo!';

/**
 * D04 · Mande o push (step 6 of 7) — the magic moment and the only screen with
 * real interaction. The composer is an optional side quest that expands in
 * place; typing updates the phone preview live. States: ready / arrived /
 * composer / fail.
 */
export default function Push() {
    const { goToNextStep, screenState, setScreenState, data, goToPreviousStep } = useOnboarding();
    const { appInfo } = useAppInfo();

    const [title, setTitle] = useState(pushTitle);
    const [text, setText] = useState(pushText);

    // The pulse that celebrates the push that has just been sent.
    const [pulseKey, setPulseKey] = useState(0);
    const [justSent, setJustSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [errors, setErrors] = useState({});
    const [deviceNotFound, setDeviceNotFound] = useState(false)

    const sentTimer = useRef(null);

    useEffect(() => () => clearTimeout(sentTimer.current), []);

    const isReady = screenState === 'ready';
    const isFail = screenState === 'fail';
    const isComposerOpen = screenState === 'composer';
    const isMerged = ['arrived', 'composer'].includes(screenState);

    const send = async (isCustom) => {
        await mobilizeiDevicesService.sendTrialPush(appInfo.platformId, appInfo.storeId, { title, text, isCustom });

        setPulseKey((k) => k + 1);
        setJustSent(true);

        clearTimeout(sentTimer.current);
        sentTimer.current = setTimeout(() => setJustSent(false), 1400);
    };

    const validate = () => {
        const nextErrors = {};
        if (!text.trim()) nextErrors.text = 'Escreva a mensagem da notificação.';

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const runSend = async (onSuccessState, isCustom) => {
        if (sending) return;

        if (!validate()) {
            setScreenState('composer');
            return;
        }

        setSending(true);
        try {
            await send(isCustom);
            if (onSuccessState) setScreenState(onSuccessState);
        } catch (error) {
            const code = error?.error?.code
            if (code === "7103") {
                setDeviceNotFound(true)
            } else {
                setDeviceNotFound(false)
            }
            showApiError(error);
            setScreenState('fail');
        } finally {
            setSending(false);
        }
    };

    const handleFirstSend = () => runSend('arrived');

    const pushSkipped = () => {
        goToNextStep()
        mixpanel.track(mixpanelEvents.ONBOARDING_PUSH_SKIPPED, {
            'platform_id': appInfo.platformId,
            'store_id': appInfo.storeId
        })
    }

    return (
        <OnbFrame step={6}>
            <Box
                sx={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
                    gap: { xs: 4, md: 7 },
                    alignItems: 'center'
                }}
            >
                <Box sx={{ maxWidth: { xs: 540, md: 700 } }}>
                    <Typography
                        component="h1"
                        sx={{ fontWeight: 700, fontSize: { xs: 32, md: 36 }, lineHeight: 1.06, letterSpacing: '-0.5px', mb: 1.5 }}
                    >
                        Mande sua primeira notificação push.
                    </Typography>

                    <Typography sx={{ fontSize: { xs: 16, md: 17 }, lineHeight: 1.5, color: COLORS.textMuted, mb: 2.25, maxWidth: 470 }}>
                        Toque no botão e veja ela chegar no seu celular em segundos.
                    </Typography>

                    {/* ready — one dominant action */}
                    {isReady && (
                        <>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleFirstSend}
                                disabled={sending}
                                startIcon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 26 }} />}
                                sx={{ maxWidth: 460, py: 2.75, fontSize: 20, borderRadius: '12px', boxShadow: '0 10px 28px rgba(60,213,104,0.4)' }}
                            >
                                {sending ? 'enviando...' : 'enviar notificação'}
                            </Button>

                            <Typography sx={{ mt: 1.5, fontSize: 14, color: COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LockOutlinedIcon sx={{ fontSize: 15 }} />
                                só você recebe este teste
                            </Typography>
                        </>
                    )}

                    {/* fail */}
                    {isFail && (
                        <>
                            {deviceNotFound ? (
                                <Button
                                    onClick={goToPreviousStep}
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        alignItems: 'flex-start',
                                        p: '14px 16px',
                                        backgroundColor: COLORS.errorBg,
                                        borderRadius: '10px',
                                        mb: 2.75,
                                        maxWidth: 470,
                                        '&:hover': { backgroundColor: COLORS.errorBg }
                                    }}
                                >
                                    <ErrorOutlineIcon sx={{ fontSize: 22, color: COLORS.errorFg, flex: 'none' }} />
                                    <Typography sx={{ fontSize: 14, lineHeight: 1.45, textAlign: 'start', color: '#000' }}>
                                        <b>Não encontramos seu celular.</b> Clique aqui para voltar ao passo anterior e baixar o Visualizador da Mobilizei.
                                    </Typography>
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: '14px 16px', backgroundColor: COLORS.errorBg, borderRadius: '10px', mb: 2.75, maxWidth: 470 }}>
                                    <ErrorOutlineIcon sx={{ fontSize: 22, color: COLORS.errorFg, flex: 'none' }} />
                                    <Typography sx={{ fontSize: 14, lineHeight: 1.45 }}>
                                        Houve um erro ao enviar a notificação. Clique no botão abaixo e tente novamente.
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleFirstSend}
                                disabled={sending}
                                startIcon={<RefreshOutlinedIcon sx={{ fontSize: 24 }} />}
                                sx={{ maxWidth: 460, py: 2.5, mb: 1, fontSize: 18, borderRadius: '12px', boxShadow: '0 10px 28px rgba(60,213,104,0.4)' }}
                            >
                                {sending ? 'enviando...' : 'reenviar notificação'}
                            </Button>

                            <Typography sx={{ mt: 2, fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted }}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={pushSkipped}
                                    sx={{
                                        p: 0,
                                        border: 0,
                                        background: 'none',
                                        font: 'inherit',
                                        color: '#000',
                                        textDecoration: 'underline',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Continuar para o próximo passo
                                </Box>
                                {' '}• você pode enviar a notificação mais tarde.
                            </Typography>
                        </>
                    )}

                    {/* arrived + composer are the same screen: it expands in place */}
                    {isMerged && (
                        <>
                            {!isComposerOpen && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1.75,
                                        alignItems: 'flex-start',
                                        p: '15px 18px',
                                        backgroundColor: 'rgba(60,213,104,0.1)',
                                        border: `1px solid ${COLORS.green}`,
                                        borderRadius: '12px',
                                        mb: 2.5,
                                        maxWidth: 480
                                    }}
                                >
                                    <CheckCircleOutlineIcon sx={{ fontSize: 26, color: COLORS.forest, flex: 'none' }} />
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.25, color: COLORS.forest }}>
                                            Chegou no seu celular!
                                        </Typography>
                                        <Typography sx={{ fontSize: 15, lineHeight: 1.4, mt: 0.25 }}>
                                            É exatamente assim que a notificação vai chegar no celular do seu cliente.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Side quest — subordinate to the primary path */}
                            {!isComposerOpen ? (
                                <ButtonBase
                                    onClick={() => setScreenState('composer')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: '13px 16px',
                                        mt: 2.25,
                                        border: `1px dashed ${COLORS.border}`,
                                        borderRadius: '10px',
                                        maxWidth: 480,
                                        width: '100%',
                                        textAlign: 'left'
                                    }}
                                >
                                    <AddCircleOutlineIcon sx={{ fontSize: 20, color: COLORS.textMuted }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Quer mandar outra notificação?</Typography>
                                        <Typography sx={{ fontSize: 13, lineHeight: 1.35, color: COLORS.textMuted }}>
                                            Escreva uma mensagem do seu jeito.
                                        </Typography>
                                    </Box>
                                    <Box sx={{ fontWeight: 700, fontSize: 13, color: COLORS.green, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                        abrir <ExpandMoreIcon sx={{ fontSize: 15 }} />
                                    </Box>
                                </ButtonBase>
                            ) : (
                                <Collapse in appear>
                                    <Box
                                        sx={{
                                            mt: 2.25,
                                            maxWidth: 480,
                                            borderRadius: '12px',
                                            border: `1px solid ${COLORS.border}`,
                                            backgroundColor: COLORS.surfaceMuted,
                                            p: 2
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                            <Typography sx={{ fontWeight: 700, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.6px', flex: 1 }}>
                                                Escreva a sua
                                            </Typography>
                                            <ButtonBase
                                                onClick={() => setScreenState('arrived')}
                                                sx={{ fontWeight: 600, fontSize: 13, color: COLORS.textMuted, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                recolher <ExpandLessIcon sx={{ fontSize: 15 }} />
                                            </ButtonBase>
                                        </Box>

                                        <Box sx={{ mb: 1.5 }}>
                                            <MbTextField
                                                label="Título"
                                                placeholder="Título da notificação"
                                                value={title}
                                                error={errors.title}
                                                onChange={(e) => {
                                                    setTitle(e.target.value.slice(0, TITLE_MAX));
                                                    setErrors((prev) => ({ ...prev, title: undefined }));
                                                }}
                                                helper={`caracteres restantes: ${TITLE_MAX - title.length}`}
                                                inputProps={{ maxLength: TITLE_MAX }}
                                            />
                                        </Box>

                                        <Box sx={{ mb: 1.75 }}>
                                            <MbTextField
                                                label="Mensagem *"
                                                placeholder="Escreva a sua mensagem"
                                                value={text}
                                                error={errors.text}
                                                onChange={(e) => {
                                                    setText(e.target.value.slice(0, TEXT_MAX));
                                                    setErrors((prev) => ({ ...prev, text: undefined }));
                                                }}
                                                helper={`caracteres restantes: ${TEXT_MAX - text.length}`}
                                                inputProps={{ maxLength: TEXT_MAX }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center', gap: 1.75, flexWrap: 'wrap' }}>
                                            <Button variant="contained" onClick={() => runSend('', true)} disabled={sending} startIcon={<SendIcon sx={{ fontSize: 18 }} />} sx={{ py: 1.375, width: { md: 'auto', xs: '100%' } }}>
                                                {sending ? 'enviando...' : 'enviar agora'}
                                            </Button>

                                            <Box sx={{ display: { md: 'flex', xs: 'none' }, flex: 1 }} />

                                            <Typography sx={{ fontSize: 13, lineHeight: 1.3, color: COLORS.textMuted, display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
                                                <LockOutlinedIcon sx={{ fontSize: 14 }} />
                                                só você recebe este teste
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Collapse>
                            )}

                            {/* Primary path — dominant and always visible */}
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={goToNextStep}
                                endIcon={<ArrowForwardIcon sx={{ fontSize: 22 }} />}
                                sx={{ maxWidth: 480, mt: 2.25, py: 2.5, fontSize: { xs: 16, sm: 18 }, borderRadius: '12px', boxShadow: '0 10px 28px rgba(60,213,104,0.4)' }}
                            >
                                seguir para os próximos passos
                            </Button>
                        </>
                    )}
                </Box>

                {/* Live preview of the draft, on desktop only: on mobile the push
                    arrives on this very device, so there is nothing to simulate. */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            width: 460,
                            height: 460,
                            background: 'radial-gradient(circle, rgba(60,213,104,0.22), transparent 70%)',
                            borderRadius: '50%'
                        }}
                    />

                    {/* Always a live preview of the draft: typing in the composer
                        updates it immediately; sending only celebrates with a pulse. */}
                    <PhoneWithPush
                        key={pulseKey}
                        scale={0.95}
                        storeName={data.storeName}
                        title={title}
                        text={text}
                        logoUrl={data.logoUrl}
                        pulse={justSent}
                        sx={{ position: 'relative', zIndex: 2 }}
                    />
                </Box>
            </Box>
        </OnbFrame >
    );
}
